/**
 * RegistrationVerification Component
 *
 * Handles email verification and password setup for new user registration.
 * User clicks verification link from email → validates token → sets password → account created.
 *
 * Project: P07 - Stripe Integration
 * Sprint: S02 - Registration Flow
 * Work Unit: WU04 - Password Setup
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';

import {
  Button,
  Input,
  Form,
  Alert,
  Link,
  Card,
  CardBody,
  CardFooter,
} from '@enfomentis/ui';
import { AppLayout } from '@/components/shared/layouts';
import { Brand } from '@enfomentis/shared/config';
import { pendingRegistrationsRepository } from '@/repositories/pendingRegistrationsRepository';
import { authService } from '@/services/auth/AuthService';
import type { RegistrationValidationResult } from '@/types/supabase/PendingRegistration';

// =============================================================================
// Types
// =============================================================================

type InvalidReason = 'not_found' | 'expired' | 'already_activated';

// =============================================================================
// Component
// =============================================================================

export function RegistrationVerification(): React.JSX.Element {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  // Token validation state
  const [isValidating, setIsValidating] = useState(true);
  const [registrationData, setRegistrationData] = useState<RegistrationValidationResult | null>(null);
  const [validationError, setValidationError] = useState<InvalidReason | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);

  // Success state
  const [isActivationComplete, setIsActivationComplete] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback(() => {
    setIsPasswordVisible((prev) => !prev);
  }, []);

  const toggleConfirmPasswordVisibility = useCallback(() => {
    setIsConfirmPasswordVisible((prev) => !prev);
  }, []);

  // Password validation
  const validatePassword = (value: string): string | null => {
    if (!value) return 'Password is required';
    if (value.length < 8) return 'Password must be at least 8 characters long';
    if (!/(?=.*[a-z])/.test(value))
      return 'Password must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(value))
      return 'Password must contain at least one uppercase letter';
    if (!/(?=.*\d)/.test(value))
      return 'Password must contain at least one number';
    return null;
  };

  // Confirm password validation
  const validateConfirmPassword = (value: string): string | null => {
    if (!value) return 'Please confirm your password';
    if (value !== formData.password) return 'Passwords do not match';
    return null;
  };

  // Validation errors (show after submit)
  const passwordError = hasSubmitted ? validatePassword(formData.password) : null;
  const confirmPasswordError = hasSubmitted
    ? validateConfirmPassword(formData.confirmPassword)
    : null;

  // Validate token on mount
  useEffect(() => {
    const validateTokenAsync = async (): Promise<void> => {
      if (!token) {
        setValidationError('not_found');
        setIsValidating(false);
        return;
      }

      try {
        const data = await pendingRegistrationsRepository.validateToken(token);

        // RPC returns array, get first result (now mapped to camelCase by repository)
        const result = Array.isArray(data) ? data[0] : data;

        if (!result || !result.isValid) {
          const reason = result?.invalidReason as InvalidReason || 'not_found';
          setValidationError(reason);
          if (!result) return;
          setRegistrationData(result);
        } else {
          setRegistrationData(result);
          setValidationError(null);
        }
      } catch (error) {
        console.error('Token validation error:', error);
        setValidationError('not_found');
      } finally {
        setIsValidating(false);
      }
    };

    validateTokenAsync();
  }, [token]);

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setHasSubmitted(true);
    setServerErrors({});

    // Validate form fields
    const passwordValidationError = validatePassword(formData.password);
    const confirmPasswordValidationError = validateConfirmPassword(formData.confirmPassword);

    if (passwordValidationError || confirmPasswordValidationError) {
      return;
    }

    if (!token || !registrationData?.registrationId) {
      setServerErrors({ general: 'Invalid registration data' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Call complete-registration Edge Function
      const response = await fetch(
        `${import.meta.env['VITE_SUPABASE_URL']}/functions/v1/complete-registration`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env['VITE_SUPABASE_ANON_KEY'],
          },
          body: JSON.stringify({
            token,
            password: formData.password,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        // Handle specific error codes
        if (result.code === 'ALREADY_ACTIVATED') {
          setServerErrors({ general: 'This account has already been activated. Please sign in.' });
        } else if (result.code === 'TOKEN_EXPIRED') {
          setServerErrors({ general: 'This verification link has expired. Please request a new one.' });
        } else if (result.code === 'USER_EXISTS') {
          setServerErrors({ general: 'An account with this email already exists. Please sign in.' });
        } else {
          setServerErrors({ general: result.error || 'Account creation failed. Please try again.' });
        }
        setIsSubmitting(false);
        return;
      }

      // Success! Set session directly from edge function response (bypasses RLS issues)
      setIsLoggingIn(true);
      setIsSubmitting(false);

      // Small delay to let the user see success before session setup
      await new Promise(resolve => setTimeout(resolve, 500));

      // Use session from edge function response via AuthService
      if (result.session?.access_token && result.session?.refresh_token) {
        const sessionResult = await authService.setSessionFromTokens(
          result.session.access_token,
          result.session.refresh_token
        );

        if (!sessionResult.success) {
          console.error('Failed to set session:', sessionResult.error);
          setIsLoggingIn(false);
          setServerErrors({ general: 'Account created but auto-login failed. Please sign in manually.' });
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }

        // Session set successfully - show completion and redirect to payment
        console.log('✅ Session set successfully, redirecting to payment');
        setIsActivationComplete(true);
        setIsLoggingIn(false);

        // Small delay for UX, then redirect to payment page directly
        setTimeout(() => {
          navigate('/payment', { replace: true });
        }, 1000);
      } else {
        // No session in response - redirect to login
        console.warn('No session in edge function response, redirecting to login');
        setIsLoggingIn(false);
        setServerErrors({ general: 'Account created. Please sign in to continue.' });
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setServerErrors({ general: 'Account creation failed. Please try again.' });
      setIsSubmitting(false);
    }
  };

  // Get error message for invalid token
  const getErrorMessage = (): string => {
    switch (validationError) {
      case 'expired':
        return 'This verification link has expired. Please request a new one.';
      case 'already_activated':
        return 'This account has already been activated. Please sign in.';
      case 'not_found':
      default:
        return 'This verification link is invalid or does not exist. Please check the link and try again.';
    }
  };

  // Render loading state
  if (isValidating) {
    return (
      <AppLayout hideFooter={true}>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-4">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-lg">Validating your verification link...</p>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // Render invalid token state
  if (validationError) {
    return (
      <AppLayout hideFooter={true}>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-4">
              <XCircle className="w-16 h-16 mx-auto text-danger" />
              <h1 className="text-xl font-semibold">Verification Failed</h1>
              <p className="text-default-600">{getErrorMessage()}</p>

              {validationError === 'expired' && registrationData?.email && (
                <div className="pt-4">
                  <p className="text-sm text-default-500 mb-4">
                    Would you like us to send a new verification link to{' '}
                    <strong>{registrationData.email}</strong>?
                  </p>
                  <Button
                    color="primary"
                    onPress={() =>
                      navigate('/check-email', {
                        state: { email: registrationData.email },
                      })
                    }
                  >
                    Request New Link
                  </Button>
                </div>
              )}

              {validationError === 'already_activated' && (
                <div className="pt-4">
                  <RouterLink to="/login">
                    <Button color="primary">Go to Sign In</Button>
                  </RouterLink>
                </div>
              )}

              {validationError === 'not_found' && (
                <div className="pt-4">
                  <RouterLink to="/register">
                    <Button color="primary" variant="bordered">
                      Start New Registration
                    </Button>
                  </RouterLink>
                </div>
              )}
            </div>
          </CardBody>
          <CardFooter className="max-w-md mx-auto justify-center border-t border-default-100">
            <div className="text-center">
              <p className="text-sm text-text-secondary">
                Need help?{' '}
                <Link
                  href={`mailto:${Brand.supportEmail}`}
                  color="primary"
                  size="sm"
                >
                  Contact support
                </Link>
              </p>
            </div>
          </CardFooter>
        </Card>
      </AppLayout>
    );
  }

  // Render logging-in state (after account created, during auto-login)
  if (isLoggingIn) {
    return (
      <AppLayout hideFooter={true}>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-4">
              <CheckCircle className="w-16 h-16 mx-auto text-success" />
              <h1 className="text-xl font-semibold">Account Created!</h1>
              <p className="text-default-600">
                Welcome, {registrationData?.fullName}!
              </p>
              <p className="text-sm text-default-500">
                Signing you in...
              </p>
              <div className="flex items-center justify-center space-x-2 text-primary">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Please wait...</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // Render success state (login complete, redirecting to payment)
  if (isActivationComplete) {
    return (
      <AppLayout hideFooter={true}>
        <Card className="items-center justify-center h-full" radius="none" shadow="none">
          <CardBody className="flex max-w-md mx-auto p-6 mt-10 text-center">
            <div className="space-y-4">
              <CheckCircle className="w-16 h-16 mx-auto text-success" />
              <h1 className="text-xl font-semibold">You&apos;re all set!</h1>
              <p className="text-default-600">
                Redirecting to complete your subscription...
              </p>
              <div className="flex items-center justify-center space-x-2 text-primary">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Please wait...</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </AppLayout>
    );
  }

  // Render password setup form
  return (
    <AppLayout hideFooter={true}>
      <Card className="items-center justify-center h-full" radius="none" shadow="none">
        <CardBody className="flex max-w-md mx-auto p-6 mt-10">
          <div className="space-y-6">
            {/* Welcome Message */}
            <div className="text-center space-y-2">
              <div className="text-primary flex justify-center mb-4">
                <Mail className="w-12 h-12" strokeWidth={1.5} />
              </div>
              <h1 className="text-xl font-semibold">Email Verified!</h1>
              <p className="text-default-600">
                Welcome, {registrationData?.fullName}! Create your password to complete
                registration.
              </p>
              <p className="text-sm text-default-500">
                Account: <strong>{registrationData?.email}</strong>
              </p>
            </div>

            <Form
              validationBehavior="aria"
              validationErrors={serverErrors}
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Password Input */}
              <Input
                type={isPasswordVisible ? 'text' : 'password'}
                label="Password"
                placeholder="Create your password"
                value={formData.password}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, password: value }))
                }
                validate={validatePassword}
                isRequired
                autoComplete="new-password"
                errorMessage={serverErrors['password'] || passwordError}
                isInvalid={!!serverErrors['password'] || !!passwordError}
                endContent={
                  <button
                    className="focus:outline-none"
                    type="button"
                    onClick={togglePasswordVisibility}
                    aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                  >
                    {isPasswordVisible ? (
                      <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                    )}
                  </button>
                }
                classNames={{
                  input: 'text-base',
                }}
              />

              {/* Confirm Password Input */}
              <Input
                type={isConfirmPasswordVisible ? 'text' : 'password'}
                label="Confirm Password"
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, confirmPassword: value }))
                }
                validate={validateConfirmPassword}
                isRequired
                autoComplete="new-password"
                errorMessage={serverErrors['confirmPassword'] || confirmPasswordError}
                isInvalid={!!serverErrors['confirmPassword'] || !!confirmPasswordError}
                endContent={
                  <button
                    className="focus:outline-none"
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    aria-label={
                      isConfirmPasswordVisible ? 'Hide password' : 'Show password'
                    }
                  >
                    {isConfirmPasswordVisible ? (
                      <Eye className="w-4 h-4 text-default-400 pointer-events-none" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-default-400 pointer-events-none" />
                    )}
                  </button>
                }
                classNames={{
                  input: 'text-base',
                }}
              />

              {/* Password Requirements */}
              <div className="text-xs text-default-500 space-y-1">
                <p>Password requirements:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>At least 8 characters long</li>
                  <li>Contains uppercase and lowercase letters</li>
                  <li>Contains at least one number</li>
                </ul>
              </div>

              {/* General Error Display */}
              {serverErrors['general'] && (
                <Alert
                  color="warning"
                  variant="flat"
                  description={serverErrors['general']}
                />
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                color="primary"
                size="lg"
                isLoading={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Creating Account...' : 'Create Account'}
              </Button>
            </Form>
          </div>
        </CardBody>

        {/* Help Text */}
        <CardFooter className="max-w-md mx-auto justify-center border-t border-default-100">
          <div className="text-center">
            <p className="text-sm text-text-secondary">
              Already have an account?{' '}
              <RouterLink to="/login">
                <Link color="primary" size="sm">
                  Sign in
                </Link>
              </RouterLink>
            </p>
          </div>
        </CardFooter>
      </Card>
    </AppLayout>
  );
}
